import {
  ApplicationConfig,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter, withInMemoryScrolling, withRouterConfig } from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideAuth } from 'continity-shared-auth';
import { AUTH_SESSION_GATEWAY, provideShell } from 'continity-shared-shell';
import { SETTINGS_SERVICE, TOAST_SERVICE } from 'continity-shared-ui';
import { COMPANY_API_BASE_URL } from 'continity-shared-company';
import { ShellConfigService } from 'continity-shared-shell';

import { routes } from './app.routes';
import { environment } from '../environments/environment';
import { PhoenixAuthGateway } from './core/auth/phoenix-auth.gateway';
import { PhoenixSettingsService } from './core/services/phoenix-settings.service';
import { PhoenixToastService } from './core/services/phoenix-toast.service';

/**
 * Phoenix-Portal-App application config.
 *
 * Auth/SSO (HTTP client + auth interceptor + config-before-auth probe) comes from
 * `provideAuth(...)` (continity-shared-auth, ADO #1608); the visual frame (i18n + theme)
 * from `provideShell(...)` (continity-shared-shell). Router + animations stay here.
 */
export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      routes,
      withInMemoryScrolling({ anchorScrolling: 'enabled', scrollPositionRestoration: 'enabled' }),
      // AB#2499: the environment workspace is a parent route (`/backup-environments/:agentId`) with section
      // children. Angular does NOT hand a child route its parent's params by default — a child that
      // reads `route.snapshot.paramMap.get('agentId')` would silently get null and fail closed (the
      // drift view under `…/configuration/observed` is exactly such a child). 'always' makes parent
      // params and data visible to every child, so a section never has to walk up the route tree by
      // hand. Safe here: no route re-uses a param or data key of an ancestor.
      withRouterConfig({ paramsInheritanceStrategy: 'always' })
    ),
    provideAnimations(),
    // Host-local PKCE login; B2C itself supplies cross-portal SSO. See PhoenixAuthGateway.
    { provide: AUTH_SESSION_GATEWAY, useClass: PhoenixAuthGateway },
    // shared-ui components (TableComponent & friends) inject SETTINGS_SERVICE; without a binding
    // the FIRST shared-ui table rendered in this app dies at construction with NG0201 and the page
    // silently shows no table at all (incident: empty /runs list while the API returned rows).
    { provide: SETTINGS_SERVICE, useExisting: PhoenixSettingsService },
    // Same seam for toasts: shared-ui ships `app-toast-container` + the TOAST_SERVICE token but
    // no implementation, so without this binding the container cannot be mounted (and any
    // shared-ui component that reaches for the token dies with NG0201).
    { provide: TOAST_SERVICE, useExisting: PhoenixToastService },
    // API base for the shared company library (the main portal supplies its ConfigService's
    // apiUrl here instead). Same base and bearer token as every other Phoenix call.
    {
      provide: COMPANY_API_BASE_URL,
      useFactory: (config: ShellConfigService) => config.apiUrl,
      deps: [ShellConfigService],
    },
    // Frame-independent auth/SSO: HTTP client + auth interceptor + config-before-auth probe.
    ...provideAuth({
      configFallback: {
        apiUrl: environment.apiUrl,
        environment: environment.production ? 'production' : 'development',
        // Cross-portal product switcher links (Option B — ADO #1543). Local fallback;
        // deployed envs inject these via the pipeline-built config.json.
        portalLinks: environment.portalLinks,
      },
    }),
    // Visual shell (i18n + theme). Relies on the HttpClient from provideAuth above.
    // SIX bundles per language, merged in order (later wins), all but the last copied in from
    // the main portal's assets (see angular.json) so they stay single-sourced:
    //   • common.json — shared-ui components emit their OWN keys under `common.*`
    //     (common.table.*, common.pagination.*, …). Loading only phoenix.json rendered every
    //     shared-ui table/pagination label as a raw key.
    //   • company_settings.json — field labels, enum option labels and validation messages of the
    //     company profile, which /settings edits against the same API as the main portal. Reusing
    //     the bundle avoids a four-language fork of texts that must not diverge.
    //   • impressum.json / legalConsent.json / privacyPolicy.json — the LEGAL texts (imprint,
    //     terms, privacy policy incl. the consent-dialog copy). Both portals face the same legal
    //     entity and the same GDPR obligations, so these texts MUST be byte-identical — reusing
    //     the main portal's bundles is the guarantee (a fork here would be a compliance bug).
    //   • phoenix.json — this app's own copy.
    // Note the app's i18n-parity spec scans `phoenix.*` refs; the shared bundles get their own
    // wiring checks there, which is exactly why the common.json gap once went unnoticed.
    ...provideShell({
      i18nPrefix: './assets/i18n/',
      i18nSuffixes: [
        '/common.json',
        '/company_settings.json',
        '/impressum.json',
        '/legalConsent.json',
        '/privacyPolicy.json',
        '/phoenix.json',
      ],
      defaultLanguage: 'en',
    }),
  ],
};
